import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import HeroCarousel, { type HeroSlide } from './components/HeroCarousel';
import MovieCard from '@/components/base/MovieCard';
import StarCard from '@/components/base/StarCard';
import SectionHeader from '@/components/base/SectionHeader';
import { movies } from '@/mocks/movies';
import { stars } from '@/mocks/stars';
import { news } from '@/mocks/news';
import { formatDate } from '@/utils/media';
import { usePageMeta } from '@/hooks/usePageMeta';

const heroSlides: HeroSlide[] = [
  {
    id: 'hero-01',
    tag: '最新上映',
    title: '光的轨迹',
    subtitle:
      '在依赖人造光源运转的城市里，一位光轨工程师发现了被隐藏的运行真相。年度讨论度最高的科幻剧情片。',
    image:
      'https://readdy.ai/api/search-image?query=Cinematic%20wide%20shot%20of%20futuristic%20city%20with%20glowing%20light%20beams%20at%20night%2C%20deep%20blue%20tones%20with%20warm%20orange%20accents%2C%20dramatic%20atmospheric%20scene%2C%20professional%20film%20production%20still%2C%20high%20detail%2C%20no%20text&width=1600&height=900&seq=hebeihome-hero-01&orientation=landscape',
    to: '/movie/m06',
    meta: '电影 · 2024 · 科幻 / 剧情 · 评分 8.8',
  },
  {
    id: 'hero-02',
    tag: '定档预告',
    title: '午夜列车',
    subtitle:
      '末班列车驶出站台后，车厢里的乘客发现彼此都指向同一桩旧事，终点站之前真相必须被揭开。',
    image:
      'https://readdy.ai/api/search-image?query=Cinematic%20wide%20shot%20of%20a%20night%20railway%20station%20with%20a%20train%2C%20moody%20blue%20lighting%20with%20warm%20lamps%2C%20atmospheric%20suspense%20mood%2C%20professional%20film%20production%20still%2C%20high%20detail%2C%20no%20text&width=1600&height=900&seq=hebeihome-hero-02&orientation=landscape',
    to: '/movie/m10',
    meta: '电影 · 2024 · 悬疑 / 惊悚 · 评分 8.3',
  },
  {
    id: 'hero-03',
    tag: '明星资料',
    title: '河北彩花 · 演员档案',
    subtitle:
      '从文艺片到科幻巨制，梳理她近年作品轨迹与表演风格，了解这位实力派演员的成长脉络。',
    image:
      'https://readdy.ai/api/search-image?query=Cinematic%20wide%20shot%20of%20an%20elegant%20empty%20stage%20with%20a%20single%20spotlight%20and%20deep%20blue%20curtains%2C%20dramatic%20atmospheric%20lighting%2C%20warm%20golden%20accents%2C%20professional%20film%20still%2C%20high%20detail%2C%20no%20text&width=1600&height=900&seq=hebeihome-hero-03&orientation=landscape',
    to: '/star/s01',
    meta: '演员 · 1979年生 · 代表作《光的轨迹》《东京往事》',
  },
];

export default function Home() {
  usePageMeta({
    title: '河北彩花影视大全—明星资料与影视作品信息',
    description:
      '河北彩花官方网站，提供最新影视作品信息、明星资料及相关动态，涵盖电影和电视剧内容，满足用户对河北彩花的全面了解需求。',
    keywords: '河北彩花,河北彩花影视,河北彩花作品,河北彩花明星资料,河北彩花电影,河北彩花电视剧,河北彩花动态',
  });

  const featuredMovies = useMemo(
    () => [...movies].sort((a, b) => b.hot - a.hot).slice(0, 8),
    [],
  );
  const featuredStars = useMemo(() => {
    const primary = stars.find((star) => star.id === 's01');
    const others = stars.filter((star) => star.id !== 's01');
    return [...(primary ? [primary] : []), ...others].slice(0, 4);
  }, []);
  const latestNews = useMemo(
    () => [...news].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 5),
    [],
  );

  return (
    <div className="w-full">
      <HeroCarousel slides={heroSlides} />

      {/* 影视作品精选 */}
      <section className="w-full bg-background-50">
        <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <SectionHeader
            icon="ri-film-line"
            title="影视作品精选"
            subtitle="最新上映与热门作品，涵盖电影与电视剧"
            action={
              <Link
                to="/category"
                className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            }
          />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4" data-product-shop>
            {featuredMovies.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </div>
      </section>

      {/* 明星资料精选 */}
      <section className="w-full bg-background-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <SectionHeader
            icon="ri-user-star-line"
            title="明星资料精选"
            subtitle="聚焦河北彩花及合作演员的作品与资料"
            action={
              <Link
                to="/stars"
                className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
              >
                全部明星
                <i className="ri-arrow-right-line"></i>
              </Link>
            }
          />
          <div className="grid grid-cols-2 gap-4 md:gap-5 lg:grid-cols-4">
            {featuredStars.map((star) => (
              <StarCard key={star.id} star={star} />
            ))}
          </div>
        </div>
      </section>

      {/* 最新动态 + 网站简介 */}
      <section className="w-full bg-background-50">
        <div className="mx-auto grid w-full max-w-7xl gap-8 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <SectionHeader
              icon="ri-newspaper-line"
              title="最新动态"
              subtitle="作品资讯与明星动态，了解河北彩花的最新消息"
              action={
                <Link
                  to="/news"
                  className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
                >
                  更多资讯
                  <i className="ri-arrow-right-line"></i>
                </Link>
              }
            />
            <ul className="flex flex-col divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
              {latestNews.map((item) => (
                <li key={item.id}>
                  <Link
                    to={`/news?open=${item.id}`}
                    className="group flex items-start gap-4 p-4 transition-colors hover:bg-background-100"
                  >
                    <span
                      className={`mt-0.5 shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        item.category === '作品资讯'
                          ? 'bg-primary-50 text-primary-700'
                          : 'bg-accent-100 text-accent-900'
                      }`}
                    >
                      {item.category}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-600 md:text-base">
                        {item.title}
                      </span>
                      <span className="mt-1 block text-xs text-foreground-400">{formatDate(item.date)}</span>
                    </span>
                    <i className="ri-arrow-right-s-line mt-1 shrink-0 text-foreground-300 transition-colors group-hover:text-primary-500"></i>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <aside className="flex flex-col gap-6">
            <div className="rounded-lg border border-background-200 bg-primary-50 p-6">
              <h3 className="font-heading text-base font-bold text-foreground-950">关于本站</h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                河北彩花影视大全是一个以影视作品与明星资料为核心的内容站，帮助影视爱好者与粉丝一站式了解作品详情、人物档案与最新动态。
              </p>
              <Link
                to="/about"
                className="mt-4 inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
              >
                了解更多
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <p className="font-heading text-2xl font-bold text-primary-600">{movies.length}</p>
                <p className="mt-1 text-xs text-foreground-500">收录影视作品</p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <p className="font-heading text-2xl font-bold text-accent-600">{stars.length}</p>
                <p className="mt-1 text-xs text-foreground-500">明星资料档案</p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <p className="font-heading text-2xl font-bold text-primary-600">{news.length}</p>
                <p className="mt-1 text-xs text-foreground-500">动态资讯条目</p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <p className="font-heading text-2xl font-bold text-accent-600">每日</p>
                <p className="mt-1 text-xs text-foreground-500">内容更新频率</p>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}